import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Box, Home, Lock, CheckCircle2, Database } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function InventoryIntelligence() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpi = data || {};
  const totalUnits = kpi.total_stock_units || 0;
  const sold = kpi.sold_units || 0;
  const available = kpi.available_units || 0;
  const blocked = kpi.blocked_units || 0;
  const projects = (kpi.stock_by_project || []).filter(p => p.total !== p.sold); // Exclude fully sold
  const hasData = totalUnits > 0;

  // Absorption % = sold / (total - blocked)
  const absorbable = totalUnits - blocked;
  const absorptionPct = absorbable > 0 ? ((sold / absorbable) * 100).toFixed(1) : 0;

  const pieData = [
    { name: 'Sold', value: sold },
    { name: 'Available', value: available },
    { name: 'Blocked', value: blocked },
  ].filter(d => d.value > 0);

  const aiContext = hasData ? `Total units: ${totalUnits}, Sold: ${sold}, Available: ${available}, Blocked: ${blocked}, Absorption: ${absorptionPct}%, Projects: ${projects.length}` : '';

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="inventory-intelligence-page">
      <div><h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Inventory Intelligence</h1></div>
      <div className="empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Inventory Data</p></div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="inventory-intelligence-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Inventory Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Stock status and absorption analysis</p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      <AIPageInsight token={token} period={period} refDate={refDate} context={aiContext} pageTitle="Inventory" />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-3">
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><Box className="h-4 w-4 text-slate-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Total Units</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{totalUnits.toLocaleString('en-IN')}</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Sold</span></div>
          <p className="text-lg sm:text-xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope' }}>{sold.toLocaleString('en-IN')}</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><Home className="h-4 w-4 text-blue-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Available</span></div>
          <p className="text-lg sm:text-xl font-bold text-blue-600" style={{ fontFamily: 'Manrope' }}>{available.toLocaleString('en-IN')}</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><Lock className="h-4 w-4 text-amber-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Blocked</span></div>
          <p className="text-lg sm:text-xl font-bold text-amber-600" style={{ fontFamily: 'Manrope' }}>{blocked.toLocaleString('en-IN')}</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><Box className="h-4 w-4 text-indigo-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Absorption</span></div>
          <p className="text-lg sm:text-xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope' }}>{absorptionPct}%</p>
          <p className="text-[9px] text-slate-400">excl. blocked</p>
        </div>
      </div>

      {/* Pie + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Status Distribution</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString('en-IN')} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Project table — exclude fully sold, absorption excl. blocked */}
        <Card className="lg:col-span-2 bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Project-wise Inventory (excl. fully sold)</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Total</th>
                    <th className="text-right py-2 px-2 font-semibold text-emerald-600">Sold</th>
                    <th className="text-right py-2 px-2 font-semibold text-blue-600">Available</th>
                    <th className="text-right py-2 px-2 font-semibold text-amber-600">Blocked</th>
                    <th className="text-right py-2 px-2 font-semibold text-indigo-600">Absorption</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => {
                    const absorbableP = p.total - p.blocked;
                    const absPct = absorbableP > 0 ? ((p.sold / absorbableP) * 100).toFixed(1) : 0;
                    return (
                      <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="py-2 px-2 font-medium text-slate-800">{p.name}</td>
                        <td className="py-2 px-2 text-right text-slate-700">{p.total}</td>
                        <td className="py-2 px-2 text-right text-emerald-600">{p.sold}</td>
                        <td className="py-2 px-2 text-right text-blue-600">{p.available}</td>
                        <td className="py-2 px-2 text-right text-amber-600">{p.blocked || '-'}</td>
                        <td className="py-2 px-2 text-right">
                          <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${absPct >= 80 ? 'bg-emerald-100 text-emerald-700' : absPct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{absPct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
